import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { VariantType } from 'notistack';
import _ from 'lodash';
import { useStyles, cx } from '../../../custom-forms';
import { CaseCalculationAlternativePart, CaseCalculationPart } from '../../../interfaces/Case';
import { WorkflowCalculation } from '../../../interfaces/Workflow';
import { Organization } from '../../../interfaces/Organization';
import { IReferential } from '../../../interfaces/IReferential';
import {
  CalculationActions,
  FiltersState,
  SortState
} from '../types/alternative-calculation.types';
import { useTranslate } from '../../../hooks/useTranslate';
import { useIncrementalList } from '../../../hooks/useIncrementalList';
import { useDwellScroll, DEFAULT_DWELL_MS } from '../../../hooks/useDwellScroll';
import { useVariantsEnabled } from '../context/CalculationFlagsContext';
import {
  compileFilters,
  offerVariants,
  queriedAlternatives,
  selectedParent,
  selectedAlternatives,
  sortAlternatives
} from '../utils/calculation.utils';
import AlternativeCalculationPartHeader from './AlternativeCalculationPartHeader';
import AlternativeCalculationVariantRow from './AlternativeCalculationVariantRow';
import AlternativeCalculationSelectedRow from './AlternativeCalculationSelectedRow';
import CalculationAlternativePartAddDialog from './CalculationAlternativePartAddDialog';
import { buildGridTemplate, GridColumnConfig } from '../config/columns';

export interface AlternativeCalculationPartProps {
  part: CaseCalculationPart;
  sort: SortState;
  filters: FiltersState;
  suppliers: Organization[];
  qualities: IReferential[];
  workflowCalculation: WorkflowCalculation | null;
  actions: CalculationActions;
  onNotify: (message: string, variant: VariantType) => void;
  onCopyPart: (part: CaseCalculationPart) => void;
  // Shows the per-line "reset filter" button (only from the main AlternativeCalculationDialog).
  enableLineFilterReset: boolean;
  alternativePartsHeight: number;
  infoTooltip: React.ReactNode | null;
  columns: GridColumnConfig[];
  canExpand: boolean;
  // Clip inner content to the card radius (order part context).
  flush: boolean;
  // Header above the expanded variants (only OrderDetailsNew supplies one; the rest pass null).
  variantsHeader: React.ReactNode;
}

const AlternativeCalculationPart: React.FC<AlternativeCalculationPartProps> = ({
  part,
  sort,
  filters,
  suppliers,
  qualities,
  workflowCalculation,
  actions,
  onNotify,
  onCopyPart,
  enableLineFilterReset,
  alternativePartsHeight,
  infoTooltip,
  columns,
  canExpand,
  flush,
  variantsHeader
}) => {
  const c = useStyles();
  const t = useTranslate();
  // Inner results scroll only takes the wheel after the pointer dwells ~0.3s; otherwise the page scrolls.
  const resultsRef = useDwellScroll(DEFAULT_DWELL_MS, part.isExpanded);
  const variantsEnabled = useVariantsEnabled();
  const showNetPrice = workflowCalculation?.alternativeNetPrice ?? false;
  // Expanded alternatives always use the alt-calc column set — scope their grid template to it.
  const bodyGridStyle = {
    ['--alt-grid-cols']: buildGridTemplate(showNetPrice, variantsEnabled)
  } as React.CSSProperties;
  const [variantOpen, setVariantOpen] = useState(false);
  const openVariantDialog = useCallback(() => setVariantOpen(true), []);
  // Per-line filter reset, cleared on the next filter change.
  const [bypassFilter, setBypassFilter] = useState(false);
  useEffect(() => setBypassFilter(false), [filters]);
  const resetLineFilter = useCallback(() => setBypassFilter(true), []);
  const allRows = queriedAlternatives(part);
  const shown = useMemo(() => {
    // Bypassed part, or no active filters (e.g. right after "clear"): skip the filter pass.
    if (bypassFilter || _.isEmpty(filters)) {
      return sortAlternatives(part, allRows, sort);
    }
    const matches = compileFilters(filters);
    return sortAlternatives(part, _.filter(allRows, (alt) => matches(part, alt)), sort);
  }, [part, allRows, filters, sort, bypassFilter]);
  const resetKey = `${part.id}|${part.isExpanded}|${sort.column}|${sort.dir}|${JSON.stringify(filters)}`;
  // Pin selected rows so an off-window selection stays rendered and scroll-to-selection finds its ref.
  const { visibleItems: visibleRows, onScroll: onResultsScroll } = useIncrementalList(shown, {
    resetKey,
    isPinned: (alt) => alt.isVariantSelected
  });
  const selectedRows = selectedAlternatives(part);
  const offerCount = offerVariants(part).length;
  const recoRow = selectedParent(part);

  const summaryText = variantsEnabled
    ? `${offerCount} ${
        offerCount === 1
          ? t('ALT_CALC_TO_SEND_ONE', 'variantă de trimis către client')
          : t('ALT_CALC_TO_SEND', 'variante de trimis către client')
      }${recoRow ? ` · ${t('ALT_CALC_RECOMMENDED', 'recomandată')}: ${recoRow.name}` : ''}`
    : `${offerCount} ${
        offerCount === 1
          ? t('ALT_CALC_STATUS_SELECTED_SELECTIONS_ONE', 'selecție')
          : t('ALT_CALC_STATUS_SELECTED_SELECTIONS', 'selecții')
      }${recoRow ? `: ${recoRow.name}` : ''}`;

  const emptyMessage = part.hasBeenInterogated
    ? allRows.length
      ? t('ALT_CALC_NO_FILTER_RESULTS', 'Niciun rezultat pentru filtrele active')
      : t('NO_RECORDS_TO_DISPLAY', 'Nu exista inregistrari de afisat')
    : t('ALT_CALC_QUERY_HINT', 'Introdu codul și apasă Interoghează pentru ofertele furnizorilor.');

  const saveAlternativePart = (alternativePart: CaseCalculationAlternativePart) => {
    actions.addAlternativePart(part.id, alternativePart);
    onNotify(`„${part.name}" · ${t('ALT_CALC_VARIANT_ADDED', 'variantă adăugată')}`, 'success');
  };

  return (
    <Box className={cx(c.groupCard, part.isExpanded && c.groupCardOpen, flush && c.groupCardFlush)}>
      <AlternativeCalculationPartHeader
        part={part}
        workflowCalculation={workflowCalculation}
        qualities={qualities}
        actions={actions}
        onNotify={onNotify}
        onCopyPart={onCopyPart}
        onResetFilter={resetLineFilter}
        canResetFilter={enableLineFilterReset && !_.isEmpty(filters) && !bypassFilter}
        onAddAlternativePart={openVariantDialog}
        infoTooltip={infoTooltip}
        columns={columns}
        canExpand={canExpand}
      />

      {variantOpen ? (
        <CalculationAlternativePartAddDialog
          open
          qualities={qualities}
          suppliers={suppliers}
          onClose={() => setVariantOpen(false)}
          onSave={saveAlternativePart}
        />
      ) : null}

      {!part.isExpanded && selectedRows.length > 0 && (
        // Collapsed selected rows use the alt-calc column template (same as expanded results)
        // so the unselect "X" lands on the trailing actions column, not mid-grid.
        <Box style={bodyGridStyle}>
          {selectedRows.map((alternativePart) => (
            <AlternativeCalculationSelectedRow
              key={`sel-${alternativePart.id}`}
              part={part}
              alternativePart={alternativePart}
              workflowCalculation={workflowCalculation}
              actions={actions}
              onNotify={onNotify}
            />
          ))}
        </Box>
      )}

      {part.isExpanded && (
        <Box className={c.resultsBody} style={bodyGridStyle}>
          {variantsHeader}
          <Box
            ref={resultsRef}
            className={c.resultsScroll}
            style={{ maxHeight: alternativePartsHeight }}
            onScroll={onResultsScroll}
          >
            {part.isLoading ? (
              <Box
                className={cx(c.emptyRow, c.row, c.justifyEnd, c.gap8)}
                style={{ justifyContent: 'center' }}
              >
                <CircularProgress size={18} thickness={5} />
              </Box>
            ) : shown.length ? (
              visibleRows.map((alternativePart) => (
                <AlternativeCalculationVariantRow
                  key={alternativePart.id}
                  part={part}
                  alternativePart={alternativePart}
                  workflowCalculation={workflowCalculation}
                  actions={actions}
                  onNotify={onNotify}
                />
              ))
            ) : (
              <Box className={c.emptyRow}>{emptyMessage}</Box>
            )}
          </Box>

          {!part.isLoading && allRows.length > 0 && (
            <Box className={c.selBar}>
              <Typography variant="body2" component="span" className={c.muted}>
                {offerCount
                  ? summaryText
                  : variantsEnabled
                  ? t('ALT_CALC_SELECT_HINT', 'Selectează variantele de trimis către client.')
                  : t('ALT_CALC_SELECT_HINT_SELECTIONS', 'Selectează piesele')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AlternativeCalculationPart;
